import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, MessageCircle } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { WHATSAPP_URL } from "../data/tour";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const openEnquire = (pkg) =>
  window.dispatchEvent(new CustomEvent("enquire:open", { detail: { pkg } }));

const inputCls =
  "w-full border-b border-vine bg-transparent py-2.5 text-sm text-cream placeholder:text-sage/50 focus:border-marigold focus:outline-none transition-colors duration-300";

const EMPTY = { name: "", phone: "", email: "", package: "kochi-to-kochi", message: "" };

export const EnquireModal = () => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      const pkg = e.detail?.pkg;
      setForm((f) => ({ ...f, package: pkg || f.package }));
      setOpen(true);
    };
    window.addEventListener("enquire:open", handler);
    return () => window.removeEventListener("enquire:open", handler);
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      if (window.fbq) window.fbq("track", "Lead");
      toast.success("Enquiry received! We'll reach out within 24 hours.");
      setForm(EMPTY);
      setOpen(false);
    } catch {
      toast.error("Something went wrong — please WhatsApp us instead.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="border-vine bg-moss text-cream sm:max-w-md" data-testid="enquire-modal">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl font-medium tracking-tight text-cream">
            Don't just dream, travel.
          </DialogTitle>
          <DialogDescription className="text-sm text-sage">
            Allow us to call you back! Share your details and a TrekPanda trip expert will reach out.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} data-testid="enquire-modal-form" className="mt-2 space-y-5">
          <div>
            <label htmlFor="modal-name" className="text-[11px] uppercase tracking-[0.25em] text-sage">Full Name</label>
            <input id="modal-name" required data-testid="modal-name-input" value={form.name} onChange={set("name")} placeholder="Aarav Sharma" className={inputCls} />
          </div>
          <div>
            <label htmlFor="modal-phone" className="text-[11px] uppercase tracking-[0.25em] text-sage">Phone / WhatsApp</label>
            <input id="modal-phone" required data-testid="modal-phone-input" value={form.phone} onChange={set("phone")} placeholder="98220 12345" className={inputCls} />
          </div>
          <div>
            <label htmlFor="modal-email" className="text-[11px] uppercase tracking-[0.25em] text-sage">Email</label>
            <input id="modal-email" type="email" required data-testid="modal-email-input" value={form.email} onChange={set("email")} placeholder="you@example.com" className={inputCls} />
          </div>
          <div>
            <label htmlFor="modal-package" className="text-[11px] uppercase tracking-[0.25em] text-sage">Preferred Package</label>
            <select id="modal-package" data-testid="modal-package-select" value={form.package} onChange={set("package")}
              className="w-full border-b border-vine bg-moss py-2.5 text-sm text-cream focus:border-marigold focus:outline-none">
              <option value="kochi-to-kochi">Kochi to Kochi — ₹21,000 + 5% GST</option>
              <option value="flight">Flight Package — ₹34,000 + taxes</option>
              <option value="train">Train Package — ₹23,000 + taxes</option>
            </select>
          </div>
          <div>
            <label htmlFor="modal-message" className="text-[11px] uppercase tracking-[0.25em] text-sage">Message (optional)</label>
            <textarea id="modal-message" rows={2} data-testid="modal-message-input" value={form.message} onChange={set("message")}
              placeholder="Travelling with 3 friends, prefer 27 Sept flight…" className={`${inputCls} resize-none`} />
          </div>
          <button type="submit" disabled={loading} data-testid="modal-submit"
            className="flex w-full items-center justify-center gap-2 rounded-full bg-marigold py-4 text-xs font-bold uppercase tracking-[0.25em] text-jungle transition-colors duration-300 hover:bg-marigold-hover disabled:opacity-60">
            {loading && <Loader2 size={14} className="animate-spin" />}
            {loading ? "Sending…" : "Enquire Now"}
          </button>
        </form>

        <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="modal-whatsapp-link"
          className="mt-3 flex items-center justify-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-sage transition-colors hover:text-marigold">
          <MessageCircle size={13} /> or WhatsApp 95297 17726
        </a>
      </DialogContent>
    </Dialog>
  );
};
